import type { SetContentResult } from "@app/commands/article/features/setContent";
import type { ArticleComponentProps } from "@components/Article/Article";
import { useFileLines } from "@components/Article/useFileLines";
import { useForceSaveHandle } from "@components/Article/useForceSaveHandle";
import { useSaveArticle } from "@components/Article/useSaveArticle";
import DiffFileInput from "@components/Atoms/FileInput/DiffFileInput/DiffFileInput";
import FileInput from "@components/Atoms/FileInput/FileInput";
import { useArticleWidthStyle } from "@components/Layouts/CatalogLayout/ArticleLayout/useArticleDimensions";
import { useRouter } from "@core/Api/useRouter";
import { FRONTMATTER_ERROR_CODE } from "@core/FileStructue/Article/FrontmatterError";
import FetchService from "@core-ui/ApiServices/FetchService";
import readResponseError from "@core-ui/ApiServices/readResponseError";
import Method from "@core-ui/ApiServices/Types/Method";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { timing } from "@core-ui/timings";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import { useLiveDiffBinding } from "@ext/git/core/Diff/logic/live/useLiveDiffBinding";
import withoutFrontMatter from "@ext/git/core/Diff/logic/live/withoutFrontMatter";
import t from "@ext/localization/locale/translate";
import { Alert, AlertButton, AlertDescription, AlertIcon, AlertTitle } from "@ui-kit/Alert";
import type { editor as MonacoEditor } from "monaco-editor";
import type * as MonacoType from "monaco-editor/esm/vs/editor/editor.api";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// `.article h5` carries heading margins here, so `asChild` keeps the styling and drops the element.
const Title = ({ children }: { children: string }) => (
	<AlertTitle asChild>
		<span>{children}</span>
	</AlertTitle>
);

/** What a raw edit is told after it has been sent: nothing yet, still unreadable, or written. */
type RawSaveState = { attempted: boolean; error: string | null };

/** The banner over a raw edit: why the properties are on screen, and what came of each save. */
const RawFileNotice = ({ state, onSave }: { state: RawSaveState; onSave: () => void }) => {
	const router = useRouter();

	if (state.attempted && !state.error)
		return (
			<Alert className="mb-4" focus="high" status="success">
				<AlertIcon icon="circle-check" />
				<Title>{t("article.raw-markdown.repaired.title")}</Title>
				<AlertDescription>{t("article.raw-markdown.repaired.desc")}</AlertDescription>
				<AlertButton onClick={() => router.pushQuery({ ...(router.query || {}), mode: undefined })}>
					{t("article.raw-markdown.repaired.open")}
				</AlertButton>
			</Alert>
		);

	return (
		<Alert className="mb-4" focus="high" status="warning">
			<AlertIcon icon="alert-circle" />
			<Title>
				{state.error ? t("article.raw-markdown.not-saved.title") : t("article.raw-markdown.broken.title")}
			</Title>
			<AlertDescription>
				{state.error ? (
					<>
						{t("article.raw-markdown.not-saved.desc")}
						<pre className="mt-2 overflow-x-auto text-xs">{state.error}</pre>
					</>
				) : (
					t("article.raw-markdown.broken.desc")
				)}
			</AlertDescription>
			<AlertButton onClick={onSave}>{t("article.raw-markdown.broken.save")}</AlertButton>
		</Alert>
	);
};

// Both widgets share this model, so the undo stack survives switching between them.
export const markdownModelPath = (articlePath: string) => `inmemory://gramax/${articlePath}`;
// Monaco returns an existing model by URI without applying the new value; per-session paths
// keep a previous baseline from showing up.
const baselineModelPath = (articlePath: string, sessionId: number) =>
	`${markdownModelPath(articlePath)}#base:${sessionId}`;
const revisionModelPath = (articlePath: string, sessionId: number) =>
	`${markdownModelPath(articlePath)}#rev:${sessionId}`;

const OPTIONS = { glyphMargin: false } as const;
const DIFF_OPTIONS = {
	renderSideBySide: false,
	useInlineViewWhenSpaceIsLimited: false,
	renderOverviewRuler: false,
	glyphMargin: false,
} as const;

export const ArticleMarkdownRenderer = (props: ArticleComponentProps<"markdown">) => (
	<MarkdownArticle key={props.data.articleProps.ref.path} {...props} />
);

const MarkdownArticle = ({ data, isReadOnly }: ArticleComponentProps<"markdown">) => {
	const articleWidthStyle = useArticleWidthStyle();
	const apiUrlCreator = ApiUrlCreatorService.value;
	const apiUrlCreatorRef = useRef(apiUrlCreator);
	apiUrlCreatorRef.current = apiUrlCreator;

	const isTemplate = useArticlePropsStore((state) => !!state.data?.template);
	const articlePath = data.articleProps.ref.path;
	const isRaw = Boolean(data.isWholeFile);
	const { label, reveal } = useFileLines(data.bodyStartLine ?? 1);
	const [saveState, setSaveState] = useState<RawSaveState>({ attempted: false, error: null });
	const content = data.content ?? "";
	const contentRef = useRef(content);
	contentRef.current = content;
	// Only the working model writes here; revision content must never reach the saved file.
	const latestValueRef = useRef(content);

	const isContentSyncRef = useRef(false);

	const syncEditorContent = useCallback((editor: MonacoEditor.IStandaloneCodeEditor) => {
		const content = contentRef.current;
		latestValueRef.current = content;
		if (editor.getValue() === content) return;

		isContentSyncRef.current = true;
		try {
			editor.setValue(content);
		} finally {
			isContentSyncRef.current = false;
		}
	}, []);

	const saves = useSaveArticle({
		articleKey: articlePath,
		catalogName: apiUrlCreator.catalogName,
		articlePath,
		delayMs: timing("saveDebounceMs"),
	});

	const saveContent = useCallback(
		(content: string) => {
			const url = apiUrlCreatorRef.current.setArticleContent(articlePath, isRaw);
			saves.onContentChanged(async () => {
				const response = await FetchService.fetch<SetContentResult>(
					url,
					content,
					MimeTypes.text,
					Method.POST,
					false,
				);

				if (!isRaw) {
					if (!response.ok) throw new Error(`markdown article save failed: ${response.status}`);
					return;
				}
				if (response.ok) return setSaveState({ attempted: true, error: null });

				const error = await readResponseError(response);
				if (error?.props?.errorCode === FRONTMATTER_ERROR_CODE)
					return setSaveState({ attempted: true, error: error.props.reason });

				ErrorConfirmService.notify(error);
			});
		},
		[articlePath, isRaw, saves],
	);

	// The raw view has nothing queued until it is saved on demand.
	const forceSave = useCallback(async () => {
		if (isRaw) saveContent(latestValueRef.current);
		await saves.flush();
	}, [isRaw, saveContent, saves]);

	useForceSaveHandle(forceSave);

	const workingEditor = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);
	const monacoRef = useRef<typeof MonacoType | null>(null);
	const viewState = useRef<MonacoEditor.ICodeEditorViewState | null>(null);
	const ownedModels = useRef(new Set<string>());
	const own = (path: string) => {
		ownedModels.current.add(path);
		return path;
	};
	// Passed to widgets once: changing the value prop makes Monaco overwrite the model.
	const initialContent = useRef(data.content ?? "");

	const { session, on } = useLiveDiffBinding({ articlePath, view: "markdown" });

	const takeViewState = useCallback(() => {
		const state = workingEditor.current?.saveViewState();
		if (state) viewState.current = state;
	}, []);
	const adopt = useCallback((editor: MonacoEditor.IStandaloneCodeEditor, monaco: typeof MonacoType) => {
		monacoRef.current = monaco;
		workingEditor.current = editor;
		// https://github.com/microsoft/monaco-editor/issues/4448
		editor.updateOptions({ glyphMargin: false });
		if (viewState.current) editor.restoreViewState(viewState.current);
	}, []);

	// Snapshot in the layout phase, before the child's passive cleanup disposes the outgoing editor.
	const revision = on ? session.revision : undefined;
	const mode = !on ? "plain" : revision ? "revision" : "diff";
	// biome-ignore lint/correctness/useExhaustiveDependencies: the snapshot is needed exactly on widget switch
	useLayoutEffect(() => () => takeViewState(), [mode]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: cleanup must run on every article change, not only on unmount
	useEffect(
		() => () => {
			const monaco = monacoRef.current;
			if (!monaco) return;
			for (const path of ownedModels.current) monaco.editor.getModel(monaco.Uri.parse(path))?.dispose();
			ownedModels.current.clear();
		},
		[articlePath],
	);

	const readOnly = isReadOnly || isTemplate;
	const onChange = (value: string) => {
		if (isContentSyncRef.current) return;
		latestValueRef.current = value;
		// Saved on demand, never on a timer: an autosave would write half-typed properties,
		// and the first one that parses turns the rest of the writes back into body writes.
		if (!isRaw) saveContent(value);
	};

	// Not in the raw view: it is saved on demand, so the editor holds text the file does not have yet.
	// biome-ignore lint/correctness/useExhaustiveDependencies: article path and content trigger a sync through contentRef
	useLayoutEffect(() => {
		if (isRaw || mode === "revision" || !workingEditor.current) return;
		syncEditorContent(workingEditor.current);
	}, [articlePath, content, syncEditorContent, isRaw]);

	return (
		<div className="w-full">
			<div className="w-full h-full justify-self-center">
				<div
					className="w-[var(--article-content-wrapper-width)] ml-[calc((var(--article-content-wrapper-width)-100%)/-2)]"
					style={articleWidthStyle}
				>
					{isRaw && <RawFileNotice onSave={() => void forceSave()} state={saveState} />}
					{mode === "plain" && (
						<FileInput
							height={"85dvh"}
							keepCurrentModel
							onChange={onChange}
							onMount={(editor, monaco) => {
								adopt(editor, monaco);
								reveal(editor);
							}}
							options={{ ...OPTIONS, readOnly, lineNumbers: label }}
							path={own(markdownModelPath(articlePath))}
							style={{ padding: "0" }}
							theme={{ dark: "article-dark", light: "light" }}
							value={initialContent.current}
						/>
					)}
					{mode === "diff" && (
						<DiffFileInput
							containerStyles={{ padding: "0" }}
							height={"85dvh"}
							modified={initialContent.current}
							modifiedModelPath={own(markdownModelPath(articlePath))}
							onChange={onChange}
							onMount={(editor, monaco) => {
								editor.getOriginalEditor().updateOptions({ glyphMargin: false });
								adopt(editor.getModifiedEditor(), monaco);
							}}
							options={{ ...DIFF_OPTIONS, readOnly }}
							original={withoutFrontMatter(session.baseline?.content ?? "")}
							originalModelPath={own(baselineModelPath(articlePath, session.id))}
						/>
					)}
					{mode === "revision" && session.baseline && (
						<DiffFileInput
							containerStyles={{ padding: "0" }}
							height={"85dvh"}
							modified={withoutFrontMatter(revision.content ?? "")}
							modifiedModelPath={own(revisionModelPath(articlePath, session.id))}
							onMount={(editor, monaco) => {
								monacoRef.current = monaco;
								editor.getOriginalEditor().updateOptions({ glyphMargin: false });
								editor.getModifiedEditor().updateOptions({ glyphMargin: false });
							}}
							options={{ ...DIFF_OPTIONS, readOnly: true }}
							original={withoutFrontMatter(session.baseline.content ?? "")}
							originalModelPath={own(baselineModelPath(articlePath, session.id))}
						/>
					)}
					{mode === "revision" && !session.baseline && (
						<FileInput
							height={"85dvh"}
							onMount={(editor, monaco) => {
								monacoRef.current = monaco;
								editor.updateOptions({ glyphMargin: false });
							}}
							options={{ ...OPTIONS, readOnly: true }}
							path={own(revisionModelPath(articlePath, session.id))}
							style={{ padding: "0" }}
							theme={{ dark: "article-dark", light: "light" }}
							value={withoutFrontMatter(revision.content ?? "")}
						/>
					)}
				</div>
			</div>
		</div>
	);
};
